import { ApiService } from '../../services/api';
import { CropEmbeddingSearchResponse, DesignResponse } from '../../types';
import {
  ApiMasterLabelBarcodeLookupResponse,
  ApiMasterLabelCropLookupResponse,
  ApiMasterLabelDesignLookupResponse,
  ApiMasterLabelLinkTargetsRequest,
  ApiMasterLabelLinkTargetsResponse,
  ApiMasterLabelUPCData,
  BarcodeStatus,
  DataCollectionCompleteBatchResponse,
  DataCollectionCreateRequest,
  DataCollectionCreateResponse,
  DataCollectionDashboardResponse,
  DataCollectionRequestedType,
  DataCollectionRequestRow,
  DataCollectionReviewBatchResponse,
  DataCollectionStatus,
  DataCollectionTargetScope,
  ForgeDesignGroupsDashboardResponse,
  ForgeDesignGroupSearchResponse,
  ForgeDesignGroupsRefreshRequest,
  ForgeDesignGroupsScopeChangeRequest,
  ForgeDesignsDashboardResponse,
  ForgeDesignsRefreshRequest,
  ForgeReviewSort,
  ForgeReviewStatusUpdateRequest,
  ForgeReviewStatusUpdateResponse,
  ForgeSupportedChainsResponse,
  ForgeSystemHealthDashboardResponse,
  ForgeSystemHealthRefreshRequest,
  ForgeSystemHealthRefreshRunResponse,
  MasterDataCollectionCreateEmbeddingCropsRequest,
  MasterDataCollectionCreateEmbeddingCropsResponse,
  MasterDataCollectionDetailResponse,
  MasterDataCollectionImagePreviewResponse,
  MasterDataCollectionMasterLabelStateSaveRequest,
  MasterDataCollectionMasterLabelStateSaveResponse,
  MasterLabelCropQueryResponse,
  MasterLabelStateSaveRequest,
  MasterLabelStateSaveResponse,
  MasterLabelUnresolvedChainSnapshot,
  MasterLabelUnresolvedDashboardResponse,
  MasterLabelUnresolvedFilter,
  MasterLabelUnresolvedWorkItemsResponse,
  ModifyForgeDesignGroupsRequest,
  ModifyForgeDesignGroupsResponse,
  ProductRecognitionInferenceAggregateResponse,
  ProductRecognitionInferenceDashboardResponse,
  ProductRecognitionInferencePlotAnnotation,
  ProductRecognitionInferencePlotAnnotationRequest,
  ProductRecognitionInferencePlotAnnotationsResponse,
  ProductRecognitionInferenceRefreshRequest,
  ProductRecognitionInferenceRefreshResponse,
  ProductRecognitionInferenceRefreshRunResponse,
  RemoteDataCollectionChainSnapshot,
  RemoteDataCollectionConfig,
  RemoteDataCollectionConfigUpdateRequest,
  RemoteDataCollectionDailyPreviewsResponse,
  RemoteDataCollectionDashboardResponse,
  RemoteDataCollectionDefaults,
  RemoteDataCollectionImageTimelineResponse,
  RemoteDataCollectionRefreshRequest,
  RemoteDataCollectionReviewTargetsResponse,
  RemoteDataCollectionRunResponse,
  RemoteDataCollectionWorkItemDetailResponse,
  RemoteDataCollectionWorkItemsResponse,
} from '../types';

function normalizeApiDlUrl(url: string): string {
  try {
    const parsedUrl = new URL(url);
    if (parsedUrl.protocol === 'http:' && parsedUrl.hostname === 'localhost') {
      parsedUrl.hostname = '127.0.0.1';
      return parsedUrl.toString().replace(/\/$/, '');
    }
  } catch {
    return url;
  }

  return url.replace(/\/$/, '');
}

const API_DL_URL = process.env.REACT_APP_API_DL_URL
  ? normalizeApiDlUrl(process.env.REACT_APP_API_DL_URL)
  : undefined;

if (!API_DL_URL) {
  throw new Error('REACT_APP_API_DL_URL is required for Forge API requests');
}

const FORGE_ADMIN_API_URL = `${API_DL_URL}/v1/forge/admin`;
const FORGE_DESIGNS_API_URL = `${API_DL_URL}/v1/forge/designs`;
const CONTROL_EMBEDDING_API_URL = `${API_DL_URL}/v1/control/embedding`;
const FORGE_DASHBOARDS_API_URL = `${FORGE_ADMIN_API_URL}/dashboards`;
const FORGE_DATA_COLLECTION_API_URL = `${FORGE_ADMIN_API_URL}/data-collection`;
const FORGE_DESIGN_GROUPS_API_URL = `${FORGE_ADMIN_API_URL}/design-groups`;
const FORGE_MASTER_DATA_COLLECTION_API_URL = `${FORGE_ADMIN_API_URL}/master-data-collection`;
const FORGE_MASTER_LABEL_UNRESOLVED_API_URL = `${FORGE_ADMIN_API_URL}/unresolved-master-label`;
const FORGE_MASTER_LABEL_API_URL = `${API_DL_URL}/v1/forge/labeling-tools/master-label`;
const FORGE_REMOTE_DATA_COLLECTION_API_URL = `${FORGE_ADMIN_API_URL}/remote-data-collection`;

const inFlightMasterLabelUnresolvedRequests = new Map<
  string,
  Promise<MasterLabelUnresolvedDashboardResponse>
>();
const inFlightRemoteDataCollectionRequests = new Map<
  string,
  Promise<RemoteDataCollectionDashboardResponse>
>();
const inFlightRemoteDataCollectionConfigRequests = new Map<
  string,
  Promise<RemoteDataCollectionDefaults>
>();
const inFlightDesignGroupsDashboardRequests = new Map<
  string,
  Promise<ForgeDesignGroupsDashboardResponse>
>();
const inFlightDesignsDashboardRequests = new Map<string, Promise<ForgeDesignsDashboardResponse>>();
const inFlightSystemHealthRequests = new Map<string, Promise<ForgeSystemHealthDashboardResponse>>();

type ApiReviewStatusUpdateResponse = {
  group_id?: string;
  groupId?: string;
  is_reviewed?: boolean;
  isReviewed?: boolean;
  reviewed_by?: string | null;
  reviewedBy?: string | null;
  reviewed_at?: string | null;
  reviewedAt?: string | null;
};

function normalizeReviewStatusUpdateResponse(
  payload: ApiReviewStatusUpdateResponse,
  groupId: string,
  fallbackIsReviewed: boolean
): ForgeReviewStatusUpdateResponse {
  return {
    groupId: payload.groupId ?? payload.group_id ?? groupId,
    isReviewed: payload.isReviewed ?? payload.is_reviewed ?? fallbackIsReviewed,
    reviewedBy: payload.reviewedBy ?? payload.reviewed_by ?? null,
    reviewedAt: payload.reviewedAt ?? payload.reviewed_at ?? null,
  };
}

const inFlightProductRecognitionInferenceRequests = new Map<
  string,
  Promise<ProductRecognitionInferenceDashboardResponse>
>();
const productRecognitionInferenceDashboardCache = new Map<
  string,
  ProductRecognitionInferenceDashboardResponse
>();
const inFlightProductRecognitionInferenceAggregateRequests = new Map<
  string,
  Promise<ProductRecognitionInferenceAggregateResponse>
>();
const productRecognitionInferenceAggregateCache = new Map<
  string,
  ProductRecognitionInferenceAggregateResponse
>();
const inFlightProductRecognitionInferenceAnnotationRequests = new Map<
  string,
  Promise<ProductRecognitionInferencePlotAnnotationsResponse>
>();
let productRecognitionInferenceCacheGeneration = 0;

function clearProductRecognitionInferenceCaches(): void {
  productRecognitionInferenceCacheGeneration += 1;
  productRecognitionInferenceDashboardCache.clear();
  productRecognitionInferenceAggregateCache.clear();
  inFlightProductRecognitionInferenceRequests.clear();
  inFlightProductRecognitionInferenceAggregateRequests.clear();
  inFlightProductRecognitionInferenceAnnotationRequests.clear();
}

function filenameFromContentDisposition(value: string | null, fallback: string): string {
  if (!value) return fallback;
  const filenameStarMatch = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (filenameStarMatch?.[1]) {
    try {
      return decodeURIComponent(filenameStarMatch[1]);
    } catch {
      return fallback;
    }
  }
  const filenameMatch = value.match(/filename="?([^";]+)"?/i);
  return filenameMatch?.[1] ?? fallback;
}

export class ForgeApiError extends Error {
  status: number;

  detail?: string;

  rawBody?: string;

  constructor(message: string, status: number, detail?: string, rawBody?: string) {
    super(message);
    this.name = 'ForgeApiError';
    this.status = status;
    this.detail = detail;
    this.rawBody = rawBody;
  }
}

type ApiErrorPayload = {
  detail?: string | { code?: string; message?: string };
  message?: string;
  errors?: Array<{ detail?: string | { code?: string; message?: string }; message?: string }>;
};

function normalizeApiErrorDetail(detail: ApiErrorPayload['detail']): string | undefined {
  if (!detail) return undefined;
  if (typeof detail === 'string') {
    try {
      const parsedDetail = JSON.parse(detail) as ApiErrorPayload['detail'];
      if (parsedDetail && typeof parsedDetail !== 'string') {
        return parsedDetail.message || parsedDetail.code || detail;
      }
    } catch {
      return detail;
    }
    return detail;
  }
  return detail.message || detail.code;
}

async function parseApiError(response: Response, fallback: string): Promise<ForgeApiError> {
  const rawBody = await response.text().catch(() => '');
  let payload: ApiErrorPayload | null = null;

  if (rawBody) {
    try {
      payload = JSON.parse(rawBody) as ApiErrorPayload;
    } catch {
      payload = null;
    }
  }

  const nestedError = payload?.errors?.find((error) => error.detail || error.message);
  const detail =
    normalizeApiErrorDetail(payload?.detail) ||
    payload?.message ||
    normalizeApiErrorDetail(nestedError?.detail) ||
    nestedError?.message ||
    rawBody ||
    undefined;
  const message = detail || `${fallback} (${response.status} ${response.statusText})`;

  return new ForgeApiError(message, response.status, detail, rawBody || undefined);
}

export class ForgeApiService {
  static async fetchSupportedChains(token: string): Promise<ForgeSupportedChainsResponse> {
    const response = await fetch(`${FORGE_ADMIN_API_URL}/supported-chains`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching supported Forge chains');
    }
    return response.json();
  }

  static async fetchDataCollectionDashboard(
    token: string,
    filters?: {
      status?: DataCollectionStatus | 'all';
      chainId?: number | null;
      targetScope?: DataCollectionTargetScope | 'all' | null;
      requestedType?: DataCollectionRequestedType | 'all' | null;
      requestedBy?: string | 'all' | null;
      groupBy?: 'item_number' | null;
    }
  ): Promise<DataCollectionDashboardResponse> {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.chainId) params.set('chain_id', String(filters.chainId));
    if (filters?.targetScope && filters.targetScope !== 'all') {
      params.set('target_scope', filters.targetScope);
    }
    if (filters?.requestedType && filters.requestedType !== 'all') {
      params.set('requested_type', filters.requestedType);
    }
    if (filters?.requestedBy && filters.requestedBy !== 'all') {
      params.set('requested_by', filters.requestedBy);
    }
    if (filters?.groupBy) params.set('group_by', filters.groupBy);
    const query = params.toString();
    const response = await fetch(
      `${FORGE_DASHBOARDS_API_URL}/data-collection${query ? `?${query}` : ''}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching data collection dashboard');
    }
    return response.json();
  }

  static async createDataCollectionRequest(
    token: string,
    request: DataCollectionCreateRequest
  ): Promise<DataCollectionCreateResponse> {
    const response = await fetch(`${FORGE_DATA_COLLECTION_API_URL}/requests`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error creating data collection request');
    }
    return response.json();
  }

  static async completeDataCollectionRequest(
    token: string,
    requestId: string
  ): Promise<DataCollectionRequestRow> {
    const response = await fetch(
      `${FORGE_DATA_COLLECTION_API_URL}/requests/${encodeURIComponent(requestId)}/complete`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error completing data collection request');
    }
    return response.json();
  }

  static async completeDataCollectionRequestsBatch(
    token: string,
    requestIds: string[]
  ): Promise<DataCollectionCompleteBatchResponse> {
    const response = await fetch(`${FORGE_DATA_COLLECTION_API_URL}/requests/complete-batch`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ request_ids: requestIds }),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error completing data collection requests');
    }
    return response.json();
  }

  static async reviewDataCollectionRequest(
    token: string,
    requestId: string
  ): Promise<DataCollectionRequestRow> {
    const response = await fetch(
      `${FORGE_DATA_COLLECTION_API_URL}/requests/${encodeURIComponent(requestId)}/review`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error reviewing data collection request');
    }
    return response.json();
  }

  static async reviewDataCollectionRequestsBatch(
    token: string,
    requestIds: string[]
  ): Promise<DataCollectionReviewBatchResponse> {
    const response = await fetch(`${FORGE_DATA_COLLECTION_API_URL}/requests/review-batch`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ request_ids: requestIds }),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error reviewing data collection requests');
    }
    return response.json();
  }

  static async fetchMasterLabelUnresolved(
    token: string
  ): Promise<MasterLabelUnresolvedDashboardResponse> {
    const existingRequest = inFlightMasterLabelUnresolvedRequests.get(token);
    if (existingRequest) {
      return existingRequest;
    }

    const request = (async (): Promise<MasterLabelUnresolvedDashboardResponse> => {
      const response = await fetch(`${FORGE_DASHBOARDS_API_URL}/master-label-unresolved`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      ApiService.handleError(response);
      if (!response.ok) {
        throw await parseApiError(response, 'Error fetching Master Label unresolved list');
      }
      return response.json();
    })();

    inFlightMasterLabelUnresolvedRequests.set(token, request);
    try {
      return await request;
    } finally {
      inFlightMasterLabelUnresolvedRequests.delete(token);
    }
  }

  static async refreshMasterLabelUnresolved(
    token: string,
    chainId: number
  ): Promise<MasterLabelUnresolvedChainSnapshot> {
    const response = await fetch(`${FORGE_DASHBOARDS_API_URL}/master-label-unresolved/refresh`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        chain_id: chainId,
      }),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error refreshing Master Label unresolved snapshot');
    }
    return response.json();
  }

  static async fetchMasterLabelUnresolvedWorkItems(
    token: string,
    params?: {
      chainId?: number | null;
      unresolvedFilter?: MasterLabelUnresolvedFilter;
      page?: number;
      limit?: number;
      signal?: AbortSignal;
    }
  ): Promise<MasterLabelUnresolvedWorkItemsResponse> {
    const query = new URLSearchParams();
    if (params?.chainId) query.set('chain_id', String(params.chainId));
    if (params?.unresolvedFilter) query.set('unresolved_filter', params.unresolvedFilter);
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    const response = await fetch(
      `${FORGE_MASTER_LABEL_UNRESOLVED_API_URL}/work-items${query.toString() ? `?${query.toString()}` : ''}`,
      { headers: { Authorization: `Bearer ${token}` }, signal: params?.signal }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching Master Label unresolved work items');
    }
    return response.json();
  }

  static async exportMasterLabelUnresolvedWorkItems(
    token: string,
    params?: { chainId?: number | null; unresolvedFilter?: MasterLabelUnresolvedFilter }
  ): Promise<{ blob: Blob; filename: string }> {
    const query = new URLSearchParams();
    if (params?.chainId) query.set('chain_id', String(params.chainId));
    if (params?.unresolvedFilter) query.set('unresolved_filter', params.unresolvedFilter);
    const response = await fetch(
      `${FORGE_MASTER_LABEL_UNRESOLVED_API_URL}/work-items/export${query.toString() ? `?${query.toString()}` : ''}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error exporting Master Label unresolved work items');
    }
    return {
      blob: await response.blob(),
      filename: filenameFromContentDisposition(
        response.headers.get('content-disposition'),
        'master-label-unresolved-work-items.csv'
      ),
    };
  }

  static async fetchRemoteDataCollection(
    token: string
  ): Promise<RemoteDataCollectionDashboardResponse> {
    const existingRequest = inFlightRemoteDataCollectionRequests.get(token);
    if (existingRequest) {
      return existingRequest;
    }

    const request = (async (): Promise<RemoteDataCollectionDashboardResponse> => {
      const response = await fetch(`${FORGE_DASHBOARDS_API_URL}/remote-data-collection`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      ApiService.handleError(response);
      if (!response.ok) {
        throw await parseApiError(response, 'Error fetching remote data collection dashboard');
      }
      return response.json();
    })();

    inFlightRemoteDataCollectionRequests.set(token, request);
    try {
      return await request;
    } finally {
      inFlightRemoteDataCollectionRequests.delete(token);
    }
  }

  static async refreshRemoteDataCollection(
    token: string,
    request: RemoteDataCollectionRefreshRequest
  ): Promise<RemoteDataCollectionChainSnapshot> {
    const response = await fetch(`${FORGE_DASHBOARDS_API_URL}/remote-data-collection/refresh`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error refreshing remote data collection snapshot');
    }
    return response.json();
  }

  static async fetchRemoteDataCollectionConfig(
    token: string,
    params?: { nonAdminPreview?: boolean }
  ): Promise<RemoteDataCollectionDefaults> {
    const requestKey = `${token}:${params?.nonAdminPreview ? 'non-admin-preview' : 'default'}`;
    const existingRequest = inFlightRemoteDataCollectionConfigRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest;
    }

    const request = (async (): Promise<RemoteDataCollectionDefaults> => {
      const query = new URLSearchParams();
      if (params?.nonAdminPreview) query.set('non_admin_preview', 'true');
      const response = await fetch(
        `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/config${query.toString() ? `?${query.toString()}` : ''}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      ApiService.handleError(response);
      if (!response.ok) {
        throw await parseApiError(response, 'Error fetching Remote DC configuration');
      }
      return response.json();
    })();

    inFlightRemoteDataCollectionConfigRequests.set(requestKey, request);
    try {
      return await request;
    } finally {
      inFlightRemoteDataCollectionConfigRequests.delete(requestKey);
    }
  }

  static async fetchRemoteDataCollectionConfigDefaults(
    token: string
  ): Promise<RemoteDataCollectionConfig> {
    const response = await fetch(`${FORGE_REMOTE_DATA_COLLECTION_API_URL}/config/defaults`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching Remote DC configuration');
    }
    return response.json();
  }

  static async updateRemoteDataCollectionConfigDefaults(
    token: string,
    request: RemoteDataCollectionConfigUpdateRequest
  ): Promise<RemoteDataCollectionConfig> {
    const response = await fetch(`${FORGE_REMOTE_DATA_COLLECTION_API_URL}/config/defaults`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error updating Remote DC configuration');
    }
    return response.json();
  }

  static async exportRemoteDataCollectionWorkItems(
    token: string,
    params?: { chainId?: number | null; taskPrefix?: string | null }
  ): Promise<{ blob: Blob; filename: string }> {
    const query = new URLSearchParams();
    if (params?.chainId) query.set('chain_id', String(params.chainId));
    if (params?.taskPrefix) query.set('task_prefix', params.taskPrefix);
    const response = await fetch(
      `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/work-items/export${query.toString() ? `?${query.toString()}` : ''}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error exporting Remote DC work items');
    }
    return {
      blob: await response.blob(),
      filename: filenameFromContentDisposition(
        response.headers.get('content-disposition'),
        'remote-dc-work-items.csv'
      ),
    };
  }

  static async fetchDesignsDashboard(
    token: string,
    filters?: {
      chainId?: number | null;
      barcodeStatus?: BarcodeStatus | null;
      query?: string | null;
      updatedBy?: string | null;
      updatedByValues?: string[] | null;
      reviewSort?: ForgeReviewSort | null;
      page?: number;
      limit?: number;
    }
  ): Promise<ForgeDesignsDashboardResponse> {
    const params = new URLSearchParams();
    if (filters?.chainId) params.set('chain_id', String(filters.chainId));
    if (filters?.barcodeStatus) params.set('barcode_status', filters.barcodeStatus);
    if (filters?.query) params.set('query', filters.query);
    if (filters?.updatedBy) params.set('updated_by', filters.updatedBy);
    filters?.updatedByValues?.forEach((value) => params.append('updated_by_values', value));
    if (filters?.reviewSort) params.set('review_sort', filters.reviewSort);
    if (filters?.page) params.set('page', String(filters.page));
    if (filters?.limit) params.set('limit', String(filters.limit));
    const query = params.toString();
    const requestKey = `${token}:${query}`;
    const existingRequest = inFlightDesignsDashboardRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest;
    }

    const request = (async (): Promise<ForgeDesignsDashboardResponse> => {
      const response = await fetch(
        `${FORGE_DASHBOARDS_API_URL}/designs${query ? `?${query}` : ''}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      ApiService.handleError(response);
      if (!response.ok) {
        throw await parseApiError(response, 'Error fetching Forge designs dashboard');
      }
      return response.json();
    })();

    inFlightDesignsDashboardRequests.set(requestKey, request);
    try {
      return await request;
    } finally {
      inFlightDesignsDashboardRequests.delete(requestKey);
    }
  }

  static async refreshDesignsDashboard(
    token: string,
    request: ForgeDesignsRefreshRequest
  ): Promise<ForgeDesignsDashboardResponse> {
    const response = await fetch(`${FORGE_DASHBOARDS_API_URL}/designs/refresh`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error refreshing Forge designs dashboard');
    }
    return response.json();
  }

  static async updateDesignReviewStatus(
    token: string,
    designId: string,
    request: ForgeReviewStatusUpdateRequest
  ): Promise<DesignResponse> {
    const response = await fetch(`${FORGE_DESIGNS_API_URL}/${designId}/review`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error updating Forge design review status');
    }
    return response.json();
  }

  static async fetchDesignGroupsDashboard(
    token: string,
    filters?: {
      chainId?: number | null;
      scope?: string | null;
      query?: string | null;
      updatedBy?: string | null;
      updatedByValues?: string[] | null;
      reviewSort?: ForgeReviewSort | null;
      page?: number;
      limit?: number;
    }
  ): Promise<ForgeDesignGroupsDashboardResponse> {
    const params = new URLSearchParams();
    if (filters?.chainId) params.set('chain_id', String(filters.chainId));
    if (filters?.scope) params.set('scope', filters.scope);
    if (filters?.query) params.set('query', filters.query);
    if (filters?.updatedBy) params.set('updated_by', filters.updatedBy);
    filters?.updatedByValues?.forEach((value) => params.append('updated_by_values', value));
    if (filters?.reviewSort) params.set('review_sort', filters.reviewSort);
    if (filters?.page) params.set('page', String(filters.page));
    if (filters?.limit) params.set('limit', String(filters.limit));
    const query = params.toString();
    const requestKey = `${token}:${query}`;
    const existingRequest = inFlightDesignGroupsDashboardRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest;
    }

    const request = (async (): Promise<ForgeDesignGroupsDashboardResponse> => {
      const response = await fetch(
        `${FORGE_DASHBOARDS_API_URL}/design-groups${query ? `?${query}` : ''}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      ApiService.handleError(response);
      if (!response.ok) {
        throw await parseApiError(response, 'Error fetching Forge design groups dashboard');
      }
      return response.json();
    })();

    inFlightDesignGroupsDashboardRequests.set(requestKey, request);
    try {
      return await request;
    } finally {
      inFlightDesignGroupsDashboardRequests.delete(requestKey);
    }
  }

  static async refreshDesignGroupsDashboard(
    token: string,
    request: ForgeDesignGroupsRefreshRequest
  ): Promise<ForgeDesignGroupsDashboardResponse> {
    const response = await fetch(`${FORGE_DASHBOARDS_API_URL}/design-groups/refresh`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error refreshing Forge design groups dashboard');
    }
    return response.json();
  }

  static async updateDesignGroupScope(
    token: string,
    groupId: string,
    request: ForgeDesignGroupsScopeChangeRequest
  ): Promise<ForgeDesignGroupsDashboardResponse> {
    const response = await fetch(`${FORGE_DASHBOARDS_API_URL}/design-groups/${groupId}/scope`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error changing Forge design group type');
    }
    return response.json();
  }

  static async updateDesignGroupReviewStatus(
    token: string,
    groupId: string,
    request: ForgeReviewStatusUpdateRequest
  ): Promise<ForgeReviewStatusUpdateResponse> {
    const response = await fetch(`${FORGE_DESIGN_GROUPS_API_URL}/${groupId}/review`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error updating Forge design group review status');
    }
    return normalizeReviewStatusUpdateResponse(await response.json(), groupId, request.is_reviewed);
  }

  static async fetchSystemHealth(
    token: string,
    chainId?: number | null
  ): Promise<ForgeSystemHealthDashboardResponse> {
    const requestKey = `${token}:${chainId ?? 'all'}`;
    const existingRequest = inFlightSystemHealthRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest;
    }

    const params = new URLSearchParams();
    if (chainId) params.set('chain_id', String(chainId));
    const query = params.toString();

    const request = (async (): Promise<ForgeSystemHealthDashboardResponse> => {
      const response = await fetch(
        `${FORGE_DASHBOARDS_API_URL}/system-health${query ? `?${query}` : ''}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      ApiService.handleError(response);
      if (!response.ok) {
        throw await parseApiError(response, 'Error fetching Forge system health dashboard');
      }
      return response.json();
    })();

    inFlightSystemHealthRequests.set(requestKey, request);
    try {
      return await request;
    } finally {
      inFlightSystemHealthRequests.delete(requestKey);
    }
  }

  static async refreshSystemHealth(
    token: string,
    request: ForgeSystemHealthRefreshRequest
  ): Promise<ForgeSystemHealthDashboardResponse> {
    const response = await fetch(`${FORGE_DASHBOARDS_API_URL}/system-health/refresh`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error refreshing Forge system health dashboard');
    }
    return response.json();
  }

  static async createSystemHealthRefreshRun(
    token: string,
    request: ForgeSystemHealthRefreshRequest
  ): Promise<ForgeSystemHealthRefreshRunResponse> {
    const response = await fetch(`${FORGE_DASHBOARDS_API_URL}/system-health/runs`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error starting Forge system health refresh');
    }
    return response.json();
  }

  static async fetchSystemHealthRefreshRun(
    token: string,
    runId: string,
    params?: { signal?: AbortSignal }
  ): Promise<ForgeSystemHealthRefreshRunResponse> {
    const response = await fetch(
      `${FORGE_DASHBOARDS_API_URL}/system-health/runs/${encodeURIComponent(runId)}`,
      { headers: { Authorization: `Bearer ${token}` }, signal: params?.signal }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching Forge system health refresh');
    }
    return response.json();
  }

  static async fetchRemoteDataCollectionWorkItems(
    token: string,
    params?: {
      chainId?: number | null;
      taskPrefix?: string | null;
      page?: number;
      limit?: number;
      signal?: AbortSignal;
    }
  ): Promise<RemoteDataCollectionWorkItemsResponse> {
    const query = new URLSearchParams();
    if (params?.chainId) query.set('chain_id', String(params.chainId));
    if (params?.taskPrefix) query.set('task_prefix', params.taskPrefix);
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    const response = await fetch(
      `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/work-items${query.toString() ? `?${query.toString()}` : ''}`,
      { headers: { Authorization: `Bearer ${token}` }, signal: params?.signal }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching remote data collection work items');
    }
    return response.json();
  }

  static async fetchRemoteDataCollectionWorkItem(
    token: string,
    workItemId: string,
    params?: { signal?: AbortSignal }
  ): Promise<RemoteDataCollectionWorkItemDetailResponse> {
    const response = await fetch(
      `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/work-items/${encodeURIComponent(workItemId)}`,
      { headers: { Authorization: `Bearer ${token}` }, signal: params?.signal }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching remote data collection work item');
    }
    return response.json();
  }

  static async createRemoteDataCollectionRun(
    token: string,
    request: RemoteDataCollectionRefreshRequest
  ): Promise<RemoteDataCollectionRunResponse> {
    const response = await fetch(`${FORGE_REMOTE_DATA_COLLECTION_API_URL}/runs`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error creating remote data collection run');
    }
    return response.json();
  }

  static async fetchRemoteDataCollectionRun(
    token: string,
    runId: string,
    params?: { signal?: AbortSignal }
  ): Promise<RemoteDataCollectionRunResponse> {
    const response = await fetch(
      `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/runs/${encodeURIComponent(runId)}`,
      { headers: { Authorization: `Bearer ${token}` }, signal: params?.signal }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching remote data collection run');
    }
    return response.json();
  }

  static async fetchMasterDataCollectionInference(
    token: string,
    inferenceId: string,
    params?: {
      chainId?: number | null;
      itemNumber?: string | null;
      normalizedBarcode?: string | null;
      similarityThreshold?: number;
      deltaThreshold?: number;
      mergeLinkedCandidates?: boolean;
      nonAdminPreview?: boolean;
      signal?: AbortSignal;
    }
  ): Promise<MasterDataCollectionDetailResponse> {
    const query = new URLSearchParams();
    if (params?.chainId) query.set('chain_id', String(params.chainId));
    if (params?.itemNumber) query.set('item_number', params.itemNumber);
    if (params?.normalizedBarcode) query.set('normalized_barcode', params.normalizedBarcode);
    if (params?.similarityThreshold !== undefined) {
      query.set('similarity_threshold', String(params.similarityThreshold));
    }
    if (params?.deltaThreshold !== undefined) {
      query.set('delta_threshold', String(params.deltaThreshold));
    }
    if (params?.mergeLinkedCandidates !== undefined) {
      query.set('merge_linked_candidates', String(params.mergeLinkedCandidates));
    }
    if (params?.nonAdminPreview) query.set('non_admin_preview', 'true');
    const response = await fetch(
      `${FORGE_MASTER_DATA_COLLECTION_API_URL}/inferences/${encodeURIComponent(
        inferenceId
      )}${query.toString() ? `?${query.toString()}` : ''}`,
      { headers: { Authorization: `Bearer ${token}` }, signal: params?.signal }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching Master Data Collection inference');
    }
    return response.json();
  }

  static async fetchMasterDataCollectionReviewTargets(
    token: string,
    params: {
      chainId: number;
      itemNumber?: string | null;
      normalizedBarcode?: string | null;
      maxStores?: number;
    }
  ): Promise<RemoteDataCollectionReviewTargetsResponse> {
    const query = new URLSearchParams();
    query.set('chain_id', String(params.chainId));
    if (params.itemNumber) query.set('item_number', params.itemNumber);
    if (params.normalizedBarcode) query.set('normalized_barcode', params.normalizedBarcode);
    if (params.maxStores !== undefined) query.set('max_stores', String(params.maxStores));

    const response = await fetch(
      `${FORGE_MASTER_DATA_COLLECTION_API_URL}/review-targets?${query.toString()}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching Remote DC review targets');
    }
    return response.json();
  }

  static async fetchMasterDataCollectionImagePreview(
    token: string,
    imageId: number,
    params?: {
      chainId?: number | null;
      itemNumber?: string | null;
      normalizedBarcode?: string | null;
      signal?: AbortSignal;
    }
  ): Promise<MasterDataCollectionImagePreviewResponse> {
    const query = new URLSearchParams();
    if (params?.chainId) query.set('chain_id', String(params.chainId));
    if (params?.itemNumber) query.set('item_number', params.itemNumber);
    if (params?.normalizedBarcode) query.set('normalized_barcode', params.normalizedBarcode);

    const response = await fetch(
      `${FORGE_MASTER_DATA_COLLECTION_API_URL}/images/${encodeURIComponent(
        String(imageId)
      )}/preview${query.toString() ? `?${query.toString()}` : ''}`,
      { headers: { Authorization: `Bearer ${token}` }, signal: params?.signal }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching Remote DC image preview');
    }
    return response.json();
  }

  static async fetchMasterDataCollectionImageTimeline(
    token: string,
    imageId: number,
    params?: {
      chainId?: number | null;
      itemNumber?: string | null;
      normalizedBarcode?: string | null;
      previousCount?: number;
      signal?: AbortSignal;
    }
  ): Promise<RemoteDataCollectionImageTimelineResponse> {
    const query = new URLSearchParams();
    if (params?.chainId) query.set('chain_id', String(params.chainId));
    if (params?.itemNumber) query.set('item_number', params.itemNumber);
    if (params?.normalizedBarcode) query.set('normalized_barcode', params.normalizedBarcode);
    if (params?.previousCount !== undefined)
      query.set('previous_count', String(params.previousCount));

    const response = await fetch(
      `${FORGE_MASTER_DATA_COLLECTION_API_URL}/images/${encodeURIComponent(
        String(imageId)
      )}/timeline${query.toString() ? `?${query.toString()}` : ''}`,
      { headers: { Authorization: `Bearer ${token}` }, signal: params?.signal }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching Remote DC image timeline');
    }
    return response.json();
  }

  static async loadMasterDataCollectionImageInference(
    token: string,
    imageId: number,
    params?: {
      chainId?: number | null;
      itemNumber?: string | null;
      normalizedBarcode?: string | null;
      similarityThreshold?: number;
      deltaThreshold?: number;
      mergeLinkedCandidates?: boolean;
      nonAdminPreview?: boolean;
      cachedOnly?: boolean;
      signal?: AbortSignal;
    }
  ): Promise<MasterDataCollectionDetailResponse> {
    const query = new URLSearchParams();
    if (params?.chainId) query.set('chain_id', String(params.chainId));
    if (params?.itemNumber) query.set('item_number', params.itemNumber);
    if (params?.normalizedBarcode) query.set('normalized_barcode', params.normalizedBarcode);
    if (params?.similarityThreshold !== undefined) {
      query.set('similarity_threshold', String(params.similarityThreshold));
    }
    if (params?.deltaThreshold !== undefined) {
      query.set('delta_threshold', String(params.deltaThreshold));
    }
    if (params?.mergeLinkedCandidates !== undefined) {
      query.set('merge_linked_candidates', String(params.mergeLinkedCandidates));
    }
    if (params?.nonAdminPreview) query.set('non_admin_preview', 'true');
    if (params?.cachedOnly !== undefined) query.set('cached_only', String(params.cachedOnly));
    const response = await fetch(
      `${FORGE_MASTER_DATA_COLLECTION_API_URL}/images/${encodeURIComponent(
        String(imageId)
      )}/load-inference${query.toString() ? `?${query.toString()}` : ''}`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        signal: params?.signal,
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error loading Remote DC product recognition');
    }
    return response.json();
  }

  static async fetchMasterDataCollectionDailyPreviews(
    token: string,
    imageId: number,
    params?: {
      chainId?: number | null;
      itemNumber?: string | null;
      normalizedBarcode?: string | null;
      daysBefore?: number;
      daysAfter?: number;
    }
  ): Promise<RemoteDataCollectionDailyPreviewsResponse> {
    const query = new URLSearchParams();
    if (params?.chainId) query.set('chain_id', String(params.chainId));
    if (params?.itemNumber) query.set('item_number', params.itemNumber);
    if (params?.normalizedBarcode) query.set('normalized_barcode', params.normalizedBarcode);
    if (params?.daysBefore !== undefined) query.set('days_before', String(params.daysBefore));
    if (params?.daysAfter !== undefined) query.set('days_after', String(params.daysAfter));

    const response = await fetch(
      `${FORGE_MASTER_DATA_COLLECTION_API_URL}/images/${encodeURIComponent(
        String(imageId)
      )}/daily-previews${query.toString() ? `?${query.toString()}` : ''}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching Remote DC image previews');
    }
    return response.json();
  }

  static async fetchMasterLabelCropQuery(
    token: string,
    cropId: string,
    params?: {
      chainId?: number | null;
      limit?: number;
    }
  ): Promise<MasterLabelCropQueryResponse> {
    const query = new URLSearchParams();
    if (params?.chainId) query.set('chain_id', String(params.chainId));
    if (params?.limit !== undefined) query.set('limit', String(params.limit));

    const response = await fetch(
      `${FORGE_MASTER_LABEL_API_URL}/crops/${encodeURIComponent(
        cropId
      )}/query${query.toString() ? `?${query.toString()}` : ''}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error querying Master Label crop');
    }
    return response.json();
  }

  static async createMasterDataCollectionEmbeddingCrops(
    token: string,
    inferenceId: string,
    request: MasterDataCollectionCreateEmbeddingCropsRequest
  ): Promise<MasterDataCollectionCreateEmbeddingCropsResponse> {
    const response = await fetch(
      `${FORGE_MASTER_DATA_COLLECTION_API_URL}/inferences/${encodeURIComponent(
        inferenceId
      )}/embedding-crops`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error creating Master Data Collection crops');
    }
    return response.json();
  }

  static async saveMasterDataCollectionMasterLabelState(
    token: string,
    inferenceId: string,
    request: MasterDataCollectionMasterLabelStateSaveRequest
  ): Promise<MasterDataCollectionMasterLabelStateSaveResponse> {
    const response = await fetch(
      `${FORGE_MASTER_DATA_COLLECTION_API_URL}/inferences/${encodeURIComponent(
        inferenceId
      )}/master-label-state`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error saving Master Data Collection Master Label state');
    }
    return response.json();
  }

  static async fetchProductRecognitionInferenceDashboard(
    token: string,
    date: string,
    hour?: number | null,
    chainId?: number | null
  ): Promise<ProductRecognitionInferenceDashboardResponse> {
    const requestKey = `${token}:${date}:${hour ?? 'combined'}:${chainId ?? 'all'}`;
    const cachedResponse = productRecognitionInferenceDashboardCache.get(requestKey);
    if (cachedResponse) {
      return cachedResponse;
    }
    const existingRequest = inFlightProductRecognitionInferenceRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest;
    }

    const params = new URLSearchParams({ date });
    if (hour !== null && hour !== undefined) params.set('hour', String(hour));
    if (chainId) params.set('chain_id', String(chainId));
    const cacheGeneration = productRecognitionInferenceCacheGeneration;
    const request = (async (): Promise<ProductRecognitionInferenceDashboardResponse> => {
      const response = await fetch(
        `${FORGE_DASHBOARDS_API_URL}/product-recognition-inference?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      ApiService.handleError(response);
      if (!response.ok) {
        throw await parseApiError(
          response,
          'Error fetching Product Recognition inference dashboard'
        );
      }
      const body = await response.json();
      if (cacheGeneration === productRecognitionInferenceCacheGeneration) {
        productRecognitionInferenceDashboardCache.set(requestKey, body);
      }
      return body;
    })();

    inFlightProductRecognitionInferenceRequests.set(requestKey, request);
    try {
      return await request;
    } finally {
      inFlightProductRecognitionInferenceRequests.delete(requestKey);
    }
  }

  static async fetchProductRecognitionInferenceAggregate(
    token: string,
    dateFrom: string,
    dateTo: string,
    hour?: number | null,
    chainId?: number | null,
    modPrefix?: string | null
  ): Promise<ProductRecognitionInferenceAggregateResponse> {
    const requestKey = `${token}:${dateFrom}:${dateTo}:${hour ?? 'combined'}:${chainId ?? 'all'}:${modPrefix ?? 'all'}`;
    const cachedResponse = productRecognitionInferenceAggregateCache.get(requestKey);
    if (cachedResponse) {
      return cachedResponse;
    }
    const existingRequest = inFlightProductRecognitionInferenceAggregateRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest;
    }

    const params = new URLSearchParams({
      date_from: dateFrom,
      date_to: dateTo,
    });
    if (hour !== null && hour !== undefined) params.set('hour', String(hour));
    if (chainId) params.set('chain_id', String(chainId));
    if (modPrefix) params.set('mod_prefix', modPrefix);
    const cacheGeneration = productRecognitionInferenceCacheGeneration;
    const request = (async (): Promise<ProductRecognitionInferenceAggregateResponse> => {
      const response = await fetch(
        `${FORGE_DASHBOARDS_API_URL}/product-recognition-inference/aggregate?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      ApiService.handleError(response);
      if (!response.ok) {
        throw await parseApiError(
          response,
          'Error fetching Product Recognition inference aggregate'
        );
      }
      const body = await response.json();
      if (cacheGeneration === productRecognitionInferenceCacheGeneration) {
        productRecognitionInferenceAggregateCache.set(requestKey, body);
      }
      return body;
    })();

    inFlightProductRecognitionInferenceAggregateRequests.set(requestKey, request);
    try {
      return await request;
    } finally {
      inFlightProductRecognitionInferenceAggregateRequests.delete(requestKey);
    }
  }

  static async fetchProductRecognitionInferencePlotAnnotations(
    token: string,
    dateFrom: string,
    dateTo: string,
    chainId?: number | null
  ): Promise<ProductRecognitionInferencePlotAnnotationsResponse> {
    const requestKey = `${token}:${dateFrom}:${dateTo}:${chainId ?? 'all'}`;
    const existingRequest = inFlightProductRecognitionInferenceAnnotationRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest;
    }

    const params = new URLSearchParams({
      date_from: dateFrom,
      date_to: dateTo,
    });
    if (chainId) params.set('chain_id', String(chainId));
    const request = (async (): Promise<ProductRecognitionInferencePlotAnnotationsResponse> => {
      const response = await fetch(
        `${FORGE_DASHBOARDS_API_URL}/product-recognition-inference/annotations?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      ApiService.handleError(response);
      if (!response.ok) {
        throw await parseApiError(
          response,
          'Error fetching Product Recognition inference daily notes'
        );
      }
      return response.json();
    })();

    inFlightProductRecognitionInferenceAnnotationRequests.set(requestKey, request);
    try {
      return await request;
    } finally {
      inFlightProductRecognitionInferenceAnnotationRequests.delete(requestKey);
    }
  }

  static async saveProductRecognitionInferencePlotAnnotation(
    token: string,
    request: ProductRecognitionInferencePlotAnnotationRequest
  ): Promise<ProductRecognitionInferencePlotAnnotation> {
    const response = await fetch(
      `${FORGE_DASHBOARDS_API_URL}/product-recognition-inference/annotations`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          selected_date: request.selectedDate,
          chain_id: request.chainId,
          note: request.note,
        }),
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error saving Product Recognition inference daily note');
    }
    return response.json();
  }

  static async refreshProductRecognitionInferenceDashboard(
    token: string,
    request: ProductRecognitionInferenceRefreshRequest
  ): Promise<ProductRecognitionInferenceRefreshResponse> {
    const response = await fetch(
      `${FORGE_DASHBOARDS_API_URL}/product-recognition-inference/refresh`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chain_id: request.chainId,
          date: request.date,
          hour: request.hour,
          hours: request.hours,
          similarity_threshold: request.similarityThreshold,
          delta_threshold: request.deltaThreshold,
          merge_linked_candidates: request.mergeLinkedCandidates,
        }),
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(
        response,
        'Error refreshing Product Recognition inference snapshot'
      );
    }
    const body = await response.json();
    clearProductRecognitionInferenceCaches();
    return body;
  }

  static async createProductRecognitionInferenceRefreshRun(
    token: string,
    request: ProductRecognitionInferenceRefreshRequest
  ): Promise<ProductRecognitionInferenceRefreshRunResponse> {
    const response = await fetch(`${FORGE_DASHBOARDS_API_URL}/product-recognition-inference/runs`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        chain_id: request.chainId,
        date: request.date,
        hour: request.hour,
        hours: request.hours,
        similarity_threshold: request.similarityThreshold,
        delta_threshold: request.deltaThreshold,
        merge_linked_candidates: request.mergeLinkedCandidates,
      }),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error starting Product Recognition inference refresh');
    }
    const body = await response.json();
    if (body.status === 'completed') clearProductRecognitionInferenceCaches();
    return body;
  }

  static async fetchProductRecognitionInferenceRefreshRun(
    token: string,
    runId: string,
    params?: { signal?: AbortSignal }
  ): Promise<ProductRecognitionInferenceRefreshRunResponse> {
    const response = await fetch(
      `${FORGE_DASHBOARDS_API_URL}/product-recognition-inference/runs/${encodeURIComponent(runId)}`,
      { headers: { Authorization: `Bearer ${token}` }, signal: params?.signal }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching Product Recognition inference refresh');
    }
    const body = await response.json();
    if (body.status === 'completed') clearProductRecognitionInferenceCaches();
    return body;
  }

  static async fetchItem(
    token: string,
    itemNumber: string,
    chainId: number
  ): Promise<ApiMasterLabelUPCData> {
    const params = new URLSearchParams({
      chain_id: String(chainId),
    });
    const response = await fetch(
      `${FORGE_MASTER_LABEL_API_URL}/item-number/${encodeURIComponent(itemNumber)}?${params}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching item data');
    }
    return response.json();
  }

  static async fetchItemByBarcode(
    token: string,
    barcodeQuery: string,
    chainId: number
  ): Promise<ApiMasterLabelBarcodeLookupResponse> {
    const params = new URLSearchParams({
      chain_id: String(chainId),
    });
    const response = await fetch(
      `${FORGE_MASTER_LABEL_API_URL}/barcode/${encodeURIComponent(barcodeQuery)}?${params}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching item data by barcode');
    }
    return response.json();
  }

  static async fetchItemByDesignId(
    token: string,
    designId: string,
    chainId: number
  ): Promise<ApiMasterLabelDesignLookupResponse> {
    const params = new URLSearchParams({
      chain_id: String(chainId),
    });
    const response = await fetch(
      `${FORGE_MASTER_LABEL_API_URL}/design/${encodeURIComponent(designId)}?${params}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching item data by design id');
    }
    return response.json();
  }

  static async saveState(
    token: string,
    request: MasterLabelStateSaveRequest
  ): Promise<MasterLabelStateSaveResponse> {
    const response = await fetch(`${FORGE_MASTER_LABEL_API_URL}/state`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error saving Master Label state');
    }
    return response.json();
  }

  static async fetchItemsByCropIds(
    token: string,
    cropIds: string[],
    chainId: number
  ): Promise<ApiMasterLabelCropLookupResponse> {
    const params = new URLSearchParams({
      chain_id: String(chainId),
    });
    cropIds.forEach((cropId) => params.append('crop_id', cropId));
    const response = await fetch(`${FORGE_MASTER_LABEL_API_URL}/crops?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching item data by crop ids');
    }
    return response.json();
  }

  static async searchEmbeddingsByCropIds(
    token: string,
    embeddingCropIds: string[]
  ): Promise<CropEmbeddingSearchResponse[]> {
    const params = new URLSearchParams();
    embeddingCropIds.forEach((id) => {
      params.append('embedding_crop_ids', id);
    });
    params.append('include_item_number', 'false');

    const response = await fetch(`${CONTROL_EMBEDDING_API_URL}/by-ids?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching embedding crops by ids');
    }
    return response.json();
  }

  static async resolveMasterLabelLinkTargets(
    token: string,
    request: ApiMasterLabelLinkTargetsRequest
  ): Promise<ApiMasterLabelLinkTargetsResponse> {
    const response = await fetch(`${FORGE_MASTER_LABEL_API_URL}/link-targets`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        chain_id: request.chainId,
        item_numbers: request.itemNumbers ?? [],
        normalized_barcodes: request.normalizedBarcodes ?? [],
        design_ids: request.designIds ?? [],
        crop_ids: request.cropIds ?? [],
      }),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error resolving Master Label link targets');
    }
    return response.json();
  }

  static async searchDesignGroups(
    token: string,
    chainId: number,
    query: string,
    page = 1,
    limit = 20
  ): Promise<ForgeDesignGroupSearchResponse> {
    const params = new URLSearchParams({
      chain_id: String(chainId),
      query,
      page: String(page),
      limit: String(limit),
    });
    const response = await fetch(`${FORGE_DESIGN_GROUPS_API_URL}/search?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error searching Forge design groups');
    }
    return response.json();
  }

  static async modifyDesignGroups(
    token: string,
    request: ModifyForgeDesignGroupsRequest
  ): Promise<ModifyForgeDesignGroupsResponse> {
    const response = await fetch(`${FORGE_DESIGN_GROUPS_API_URL}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error updating Forge design groups');
    }
    return response.json();
  }
}
